import styled from "@emotion/styled";
import { convert, getUnixEpochSeconds } from "@stepfinance/common";
import type { StepColumnsType } from "@stepfinance/react";
import {
  ContextButton,
  darkGrey4,
  greenTag,
  redTag,
  StepTable,
  TagInfo,
  TokenTag,
  transparentWhite,
} from "@stepfinance/react";
import type { Dispatch, FC } from "react";
import { useMemo } from "react";
import Countdown from "react-countdown";

import { useEnrichedWallet } from "../../../contexts/enrichedWallet";
import { useLanguageContext } from "../../../contexts/language/language";
import { useNavigationContext } from "../../../contexts/navigation";
import { useRefreshContext } from "../../../contexts/refresh";
import type { RewardOption } from "../../../hooks";
import { getTokenByMint } from "../../../utils/tokenInfo";
import { countdownRenderString } from "../../../utils/utils";
import { HistoryIcon } from "../../Icons";
import CellValue from "../../Table/CellValue";
import { TokenIcon } from "../../TokenIcon";

interface RewardContractTableProps {
  contracts: RewardOption[];
  stepPrice: number | null;
  setExerciseOption: Dispatch<React.SetStateAction<RewardOption | null>>;
}

export const RewardContractTable: FC<RewardContractTableProps> = ({ contracts, stepPrice, setExerciseOption }) => {
  const { watching } = useEnrichedWallet();
  const { refreshID } = useRefreshContext();
  const { navigate } = useNavigationContext();

  // add refreshID to deps to trigger refresh on countown
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const timeUntilRewardOptions = useMemo(() => getNextRewardOptionEval(), [refreshID]);
  const {
    currentLanguageData: {
      common: { claim: claimText },
      pages: {
        dashboard: {
          modules: {
            rewardOptions: { table: rewardOptionTableText, eligibleActions, newOptionsIn },
          },
        },
      },
    },
  } = useLanguageContext();

  const stakingTableColumns: StepColumnsType<RewardOption> = [
    {
      title: rewardOptionTableText.becauseYouDid,
      key: "type",
      dataIndex: "type",
      render(_, record) {
        let rewardTag = record.type;
        switch (record.type) {
          case "LP Option":
            rewardTag = eligibleActions.providedLiquidity;
            break;
          case "Swap Option":
            rewardTag = eligibleActions.swapped;
            break;
        }
        return record.type ? (
          <TokenTagContainer>
            <TagInfo useSpaceMono={false} uppercase={false} tagType="text">
              {rewardTag}
            </TagInfo>
          </TokenTagContainer>
        ) : null;
      },
    },
    {
      title: rewardOptionTableText.youCanBuy,
      key: "amount",
      dataIndex: "amount",
      className: "column-number",
      render(_, record) {
        const { amount, rewardMint, rewardDecimals } = record;
        const symbol = getTokenByMint(rewardMint)?.symbol || "";
        const balance = convert(amount, rewardDecimals);

        return (
          <RewardOptionCell>
            <CellValue cellType="number">{balance}</CellValue>
            <TokenTagContainer>
              <TokenTag icon={<TokenIcon size={16} mintAddress={rewardMint ?? ""} />} text={symbol} />
            </TokenTagContainer>
          </RewardOptionCell>
        );
      },
    },
    {
      title: rewardOptionTableText.atAStrikePriceOf,
      key: "strkePrice",
      dataIndex: "strikePrice",
      className: "column-number",
      render(_, { strikePrice, priceDecimals }) {
        const price = convert(strikePrice, priceDecimals);
        let tagColor;
        let tagText;
        if (stepPrice === null) {
          tagColor = darkGrey4;
          tagText = rewardOptionTableText.outOfTheMoney;
        } else {
          const inTheMoney = Math.sign(stepPrice - price) > 0;
          if (inTheMoney) {
            tagColor = greenTag;
            tagText = rewardOptionTableText.inTheMoney;
          } else {
            tagColor = redTag;
            tagText = rewardOptionTableText.outOfTheMoney;
          }
        }

        return (
          <>
            <TagInfoContainer>
              <TagInfo useSpaceMono={false} uppercase={false} tagType="text" tagColorOverride={tagColor}>
                {tagText}
              </TagInfo>
            </TagInfoContainer>
            <CellValue cellType="value">{price}</CellValue>
          </>
        );
      },
    },
    {
      title: rewardOptionTableText.beforeThisDate,
      key: "expiry",
      dataIndex: "expiry",
      className: "column-number",
      render(_, { expiry }) {
        return (
          <Countdown
            date={expiry * 1000}
            renderer={(args) => <CellValue cellType="number">{countdownRenderString(args)}</CellValue>}
          ></Countdown>
        );
      },
      defaultSortOrder: "ascend",
      sortDirections: [],
      sorter: (a, b) => a.expiry - b.expiry,
      showSorterTooltip: false,
    },
    {
      key: "claim",
      dataIndex: "claim",
      render(_, record) {
        const optionValue = getOptionValue(record, stepPrice);
        return (
          <>
            {optionValue > 0 && !watching && (
              <ContextButton onClick={() => setExerciseOption(record)}>{claimText}</ContextButton>
            )}
          </>
        );
      },
    },
    {
      title: rewardOptionTableText.markingThisOptionWorth,
      key: "value",
      dataIndex: "value",
      className: "column-number",
      render(_, record) {
        return (
          <CellValue cellType="value" fontWeight="bold">
            {getOptionValue(record, stepPrice)}
          </CellValue>
        );
      },
    },
  ];
  return (
    <TableContainer>
      <StepTable<RewardOption>
        rowKey={(record) => `${record.index}-${record.distributorAddress}`}
        columns={stakingTableColumns}
        dataSource={contracts}
        pagination={false}
      />
      <ButtonBox>
        <OptionsCountdown>
          {newOptionsIn}
          <TagInfo>
            <HistoryIcon />
            <Countdown
              date={timeUntilRewardOptions}
              renderer={(args) => <TimeUntilOptions>{countdownRenderString(args)}</TimeUntilOptions>}
            ></Countdown>
          </TagInfo>
        </OptionsCountdown>
        <MoreInfoBtn>
          <ContextButton onClick={() => navigate("/reward-option-stats")}>More Info...</ContextButton>
        </MoreInfoBtn>
      </ButtonBox>
    </TableContainer>
  );
};

const ButtonBox = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const MoreInfoBtn = styled.div`
  margin-top: 0.5rem;
`;

// Prevent user from cancelling sorting on "Before" column.
const TableContainer = styled.div`
  thead.ant-table-thead tr .ant-table-cell {
    pointer-events: none;
  }
`;

const TokenTagContainer = styled.div`
  margin-left: 10px;
  span {
    margin: 6px 0;
  }
`;

const RewardOptionCell = styled.div`
  display: flex;
  justify-content: flex-end;
  .time {
    white-space: nowrap;
  }
`;

const TagInfoContainer = styled.div`
  display: inline-block;
  margin-right: 10px;
  span {
    margin: 6px 0;
  }
`;

const OptionsCountdown = styled.span`
  align-items: center;
  color: ${transparentWhite};
  display: flex;
  margin-top: 0.5rem;
  font-size: 10px;
  font-weight: bold;
  .ant-tag {
    margin-left: 0.5rem;

    svg {
      height: 12px;
      width: 12px;
    }
  }
`;

const TimeUntilOptions = styled.span`
  font-size: 14px;
  text-transform: lowercase;
`;

export const getOptionValue = (option: RewardOption, stepPrice: number | null) => {
  const { amount, strikePrice, rewardDecimals, priceDecimals } = option;
  const balance = convert(amount, rewardDecimals);
  const price = convert(strikePrice, priceDecimals);
  const optionCost = balance * price;
  const marketValue = balance * (stepPrice ?? 0);
  const optionValue = marketValue - optionCost;
  return optionValue > 0 ? optionValue : 0;
};

// past epoch time of reward options evaluation
const REWARD_OPTIONS_START_DATE = 1660262400;
// interval in days between reward options evaluations
const rewardOptionsInterval = 5;
export const getNextRewardOptionEval = () => {
  const now = getUnixEpochSeconds();
  const daysSinceStart = (now - REWARD_OPTIONS_START_DATE) / 86400;
  const intervalCount = Math.ceil(daysSinceStart / rewardOptionsInterval);
  const evalTime = REWARD_OPTIONS_START_DATE + intervalCount * 5 * 86400;
  return evalTime * 1000;
};
